import {
  getAmazonNTBTransactionsKPI,
  getAmazonTransactionsKPI,
  LagOption
} from '@blisspointmedia/bpm-types/dist/StreamingPerformance';
import { capitalize } from 'inflected';
import {
  ColumnOrMetricName,
  DimensionColNames,
  getConfig,
  getDataColForMetadata,
  getDimensionColForMetadata
} from '@blisspointmedia/bpm-types/dist/Columns';
import { Metric, NativeCurrencyMetric } from '@blisspointmedia/bpm-types/dist/CrossChannel';
import { CompanyFiscalCal } from '@blisspointmedia/bpm-types/dist/FiscalCalendar';
import { toPrettyNumber } from '@blisspointmedia/bpm-types/dist/formatUtils';
import {
  AggregatorDatum,
  MASTER_METRIC_MAP,
  MasterMetricName
} from '@blisspointmedia/bpm-types/dist/Metrics';
import {
  IMP_IN_THOUSANDS_FORMATTER,
  isCAD,
  isGBP,
  metricFormatter,
  metricFormatterGBP
} from '@blisspointmedia/bpm-types/dist/metricFormatUtils';
import { DimensionColumn } from '@blisspointmedia/bpm-types/dist/MetricsTable';
import * as P from '@blisspointmedia/bpm-types/dist/Performance';
import { App2Channels } from '@blisspointmedia/bpm-types/dist/ScpChannels';
import {
  AdInfoRow,
  CDN,
  CellData,
  ColumnMetaData,
  ColumnMetaDataMap,
  DimensionMap
} from '@Components/MetricsTable/metrics-table-utils';
import {
  DELIVERY_CURRENCY_CODE,
  METRIC_TO_PRETTY_NAME,
  PERFORMANCE_CURRENCY_CODE
} from '@Pages/CrossChannel/cross-channel-constants';
import { getDimensionIcon, getDimensionIconCell } from '@src/utils/columnMetricSystem/adapters';
import { formatDateLabel } from '@src/utils/date-utils';
import { withRatioAggregators } from '@src/utils/metrics/ratioAggregation';

const {
  CLICKS,
  IMPRESSIONS,
  VOLUME,
  SPEND,
  REVENUE,
  CPC,
  CPM,
  ROAS,
  CPX,
  CTR,
  CLICKS_CONVERSION_RATE,
  IMPS_CONVERSION_RATE,
  AOV
} = Metric;

const { CPC_NATIVE, CPM_NATIVE, CPX_NATIVE, SPEND_NATIVE, REVENUE_NATIVE, AOV_NATIVE } =
  NativeCurrencyMetric;

// Pull a derived metric's ColumnMetaData straight from bpm-types' MASTER_METRIC_MAP
// so prettyName / formatter / requiredColumns stay in sync with the canonical
// column-metric system. Ratio client math comes from `withRatioAggregators`.
const fromMaster = (name: MasterMetricName): ColumnMetaData => {
  const master = MASTER_METRIC_MAP[name];
  return {
    prettyName: master.prettyName,
    formatter: master.formatter,
    requiredColumns: master.requiredColumns
  };
};

const fromMasterWithCurrency = (
  name: MasterMetricName,
  currencyCodeKey: string
): ColumnMetaData => {
  const base = fromMaster(name);
  return {
    ...base,
    requiredColumns: [...(base.requiredColumns ?? []), currencyCodeKey],
    currencyGetter: col => (col[currencyCodeKey] === '' ? 'USD' : (col[currencyCodeKey] as string))
  };
};

// Base (non-derived) column: display fields straight from the bpm-types column config.
const fromConfig = (name: ColumnOrMetricName): ColumnMetaData => {
  const { prettyName, formatter } = getConfig(name);
  return { prettyName, formatter };
};

// Opaque metric (window-normalized or parameterized): no client math, so the server value or `--`.
const serverOnly = (name: MasterMetricName): ColumnMetaData => ({
  ...fromMaster(name),
  aggregator: () => null
});

export const sharedDimensionColumnMetaDataMap: Partial<Record<string, ColumnMetaData>> = {
  Date: {
    contentType: 'text',
    name: 'Date',
    prettyName: 'Date'
  }
};

export const sharedColumnMetaDataMap: Partial<Record<P.ColumnType, ColumnMetaData>> = {
  [CLICKS]: {
    prettyName: METRIC_TO_PRETTY_NAME[CLICKS],
    formatter: metricFormatter(CLICKS)
  },
  [SPEND]: {
    prettyName: METRIC_TO_PRETTY_NAME[SPEND],
    formatter: metricFormatter(SPEND)
  },
  [IMPRESSIONS]: {
    prettyName: 'Impressions (000s)',
    formatter: IMP_IN_THOUSANDS_FORMATTER
  },
  [VOLUME]: {
    prettyName: METRIC_TO_PRETTY_NAME[VOLUME],
    formatter: metricFormatter(VOLUME)
  },
  [REVENUE]: {
    prettyName: METRIC_TO_PRETTY_NAME[REVENUE],
    formatter: metricFormatter(REVENUE)
  },
  [CPC]: fromMaster('cpc'),
  [CPM]: fromMaster('cpm'),
  [ROAS]: fromMaster('roas'),
  [CPX]: fromMaster('cpx'),
  [CTR]: fromMaster('ctr'),
  [CLICKS_CONVERSION_RATE]: fromMaster('clicksConversionRate'),
  [IMPS_CONVERSION_RATE]: fromMaster('impsConversionRate'),
  [SPEND_NATIVE]: {
    prettyName: METRIC_TO_PRETTY_NAME[SPEND_NATIVE],
    formatter: metricFormatter(SPEND_NATIVE),
    currencyGetter: col =>
      col[DELIVERY_CURRENCY_CODE] === '' ? 'USD' : (col[DELIVERY_CURRENCY_CODE] as string),
    requiredColumns: [DELIVERY_CURRENCY_CODE, SPEND_NATIVE]
  },
  [REVENUE_NATIVE]: {
    prettyName: METRIC_TO_PRETTY_NAME[REVENUE_NATIVE],
    formatter: metricFormatter(REVENUE_NATIVE),
    currencyGetter: col =>
      col[PERFORMANCE_CURRENCY_CODE] === '' ? 'USD' : (col[PERFORMANCE_CURRENCY_CODE] as string),
    requiredColumns: [PERFORMANCE_CURRENCY_CODE, REVENUE_NATIVE]
  },
  [CPC_NATIVE]: fromMasterWithCurrency('cpcNative', DELIVERY_CURRENCY_CODE),
  [CPM_NATIVE]: fromMasterWithCurrency('cpmNative', DELIVERY_CURRENCY_CODE),
  [CPX_NATIVE]: fromMasterWithCurrency('cpxNative', DELIVERY_CURRENCY_CODE),
  [AOV_NATIVE]: fromMasterWithCurrency('aovNative', PERFORMANCE_CURRENCY_CODE),
  [AOV]: fromMaster('aov'),
  video_views: {
    prettyName: 'Video Views',
    formatter: toPrettyNumber
  },
  videos_watched_at_25p: {
    prettyName: 'Videos Watched at 25%',
    formatter: toPrettyNumber
  },
  videos_watched_at_50p: {
    prettyName: 'Videos Watched at 50%',
    formatter: toPrettyNumber
  },
  videos_watched_at_75p: {
    prettyName: 'Videos Watched at 75%',
    formatter: toPrettyNumber
  },
  videos_watched_at_100p: {
    prettyName: 'Videos Watched at 100%',
    formatter: toPrettyNumber
  },
  video_plays: {
    prettyName: 'Video Plays',
    formatter: toPrettyNumber
  },
  video_views_2_sec: {
    prettyName: 'Videos Views 2 Seconds',
    formatter: toPrettyNumber
  },
  video_views_3_sec: {
    prettyName: 'Videos Views 3 Seconds',
    formatter: toPrettyNumber
  },
  video_views_5_sec: {
    prettyName: 'Videos Views 5 Seconds',
    formatter: toPrettyNumber
  },
  video_views_6_sec: {
    prettyName: 'Videos Views 6 Seconds',
    formatter: toPrettyNumber
  },
  video_views_10_sec: {
    prettyName: 'Videos Views 10 Seconds',
    formatter: toPrettyNumber
  },
  video_views_15_sec: {
    prettyName: 'Videos Views 15 Seconds',
    formatter: toPrettyNumber
  },
  video_thruplays: {
    prettyName: 'Videos ThruPlays',
    formatter: toPrettyNumber
  },
  acos: fromMaster('acos'),
  deliveries: {
    prettyName: 'Deliveries',
    formatter: metricFormatter(CLICKS)
  },
  sends: {
    prettyName: 'Sends',
    formatter: metricFormatter(CLICKS)
  },
  opens: {
    prettyName: 'Opens',
    formatter: metricFormatter(CLICKS)
  },
  unsubscribes: {
    prettyName: 'Unsubscribes',
    formatter: metricFormatter(CLICKS)
  },
  bounces: {
    prettyName: 'Bounces',
    formatter: metricFormatter(CLICKS)
  },
  spam_abuse_reports: {
    prettyName: 'Spam Reports',
    formatter: metricFormatter(CLICKS)
  },
  ctrEmail: fromMaster('ctrEmail'),
  ctor: {
    prettyName: 'CTOR',
    formatter: metricFormatter(CTR),
    aggregator: (datum: AggregatorDatum) =>
      datum[CLICKS] && datum.opens ? (datum[CLICKS] as number) / (datum.opens as number) : null,
    requiredColumns: [CLICKS, 'opens']
  },
  revenuePerDelivered: fromMaster('revenuePerDelivered'),
  revenuePerDeliveredNative: fromMasterWithCurrency(
    'revenuePerDeliveredNative',
    PERFORMANCE_CURRENCY_CODE
  ),
  bounceRate: fromMaster('bounceRate'),
  openRate: fromMaster('openRate'),
  unsubscribeRate: fromMaster('unsubscribeRate'),
  clickToOpenRate: fromMaster('clickToOpenRate'),
  spamReportRate: fromMaster('spamReportRate'),
  // TODO remove once streaming / linear are migrated to standard SCP
  cost: {
    prettyName: METRIC_TO_PRETTY_NAME[SPEND],
    formatter: metricFormatter(SPEND)
  },
  count: {
    prettyName: 'Impressions',
    formatter: metricFormatter(VOLUME)
  },
  eCpm: {
    prettyName: 'eCPM',
    formatter: metricFormatter(CPM),
    aggregator: (datum: AggregatorDatum) =>
      datum.count && datum.cost ? ((datum.cost as number) * 1000) / (datum.count as number) : null,
    requiredColumns: ['cost', 'count']
  },
  youtubeCpm: {
    prettyName: 'CPM',
    formatter: metricFormatter(CPM),
    aggregator: (datum: AggregatorDatum) =>
      datum.count && datum.cost ? ((datum.cost as number) * 1000) / (datum.count as number) : null,
    requiredColumns: ['cost', 'count']
  },
  cpv: fromMaster('cpv'),
  viewsConversionRate: fromMaster('viewsConversionRate'),
  viewthroughRate: fromMaster('viewthroughRate'),

  // TVAD (streaming / audio / display / linear / radio) columns, from the bpm-types configs
  responding_impressions: fromConfig('responding_impressions'),
  residential_impressions: fromConfig('residential_impressions'),
  rated_spend: fromConfig('rated_spend'),
  spots: fromConfig('spots'),
  completions: fromConfig('completions'),
  completionRate: fromMaster('completionRate'),
  avgFrequency: fromMaster('avgFrequency'),
  medianFrequency: fromMaster('medianFrequency'),
  stdDevFrequency: fromMaster('stdDevFrequency'),
  reach: fromMaster('reach'),
  volume_direct: fromConfig('volume_direct'),
  volume_direct_unadjusted: fromConfig('volume_direct_unadjusted'),
  volume_direct_incremental: fromConfig('volume_direct_incremental'),
  volume_full_funnel: fromConfig('volume_full_funnel'),
  volume_full_funnel_unadjusted: fromConfig('volume_full_funnel_unadjusted'),
  volume_full_funnel_incremental: fromConfig('volume_full_funnel_incremental'),
  revenue_direct: fromConfig('revenue_direct'),
  revenue_direct_unadjusted: fromConfig('revenue_direct_unadjusted'),
  revenue_direct_incremental: fromConfig('revenue_direct_incremental'),
  revenue_full_funnel: fromConfig('revenue_full_funnel'),
  revenue_full_funnel_unadjusted: fromConfig('revenue_full_funnel_unadjusted'),
  revenue_full_funnel_incremental: fromConfig('revenue_full_funnel_incremental'),
  roasDirect: fromMaster('roasDirect'),
  roasDirectUnadjusted: fromMaster('roasDirectUnadjusted'),
  roasDirectIncremental: fromMaster('roasDirectIncremental'),
  roasFullFunnel: fromMaster('roasFullFunnel'),
  roasFullFunnelUnadjusted: fromMaster('roasFullFunnelUnadjusted'),
  roasFullFunnelIncremental: fromMaster('roasFullFunnelIncremental'),
  cpxDirect: fromMaster('cpxDirect'),
  cpxDirectUnadjusted: fromMaster('cpxDirectUnadjusted'),
  cpxDirectIncremental: fromMaster('cpxDirectIncremental'),
  cpxFullFunnel: fromMaster('cpxFullFunnel'),
  cpxFullFunnelUnadjusted: fromMaster('cpxFullFunnelUnadjusted'),
  cpxFullFunnelIncremental: fromMaster('cpxFullFunnelIncremental'),
  impsConversionRateDirect: fromMaster('impsConversionRateDirect'),
  impsConversionRateDirectUnadjusted: fromMaster('impsConversionRateDirectUnadjusted'),
  impsConversionRateDirectIncremental: fromMaster('impsConversionRateDirectIncremental'),
  impsConversionRateFullFunnel: fromMaster('impsConversionRateFullFunnel'),
  impsConversionRateFullFunnelUnadjusted: fromMaster('impsConversionRateFullFunnelUnadjusted'),
  impsConversionRateFullFunnelIncremental: fromMaster('impsConversionRateFullFunnelIncremental'),
  percentIncremental: fromMaster('percentIncremental'),
  percentDirectIncremental: fromMaster('percentDirectIncremental'),
  avgRevenueDirect: fromMaster('avgRevenueDirect'),
  avgRevenueDirectIncremental: fromMaster('avgRevenueDirectIncremental'),
  avgRevenueFullFunnel: fromMaster('avgRevenueFullFunnel'),
  avgRevenueFullFunnelIncremental: fromMaster('avgRevenueFullFunnelIncremental'),
  eCPM: fromMaster('eCPM'),
  cpxFractional: serverOnly('cpxFractional'),
  streamingRespIndex: serverOnly('streamingRespIndex'),
  linearRespIndex: serverOnly('linearRespIndex'),
  targetRatingPoints: serverOnly('targetRatingPoints')
};

export const cadOverrideColumnMetaDataMap: Partial<Record<P.ColumnType, ColumnMetaData>> = {
  [SPEND]: {
    ...(sharedColumnMetaDataMap[SPEND] as ColumnMetaData),
    formatter: metricFormatter(SPEND, true)
  },
  [REVENUE]: {
    ...(sharedColumnMetaDataMap[REVENUE] as ColumnMetaData),
    formatter: metricFormatter(REVENUE, true)
  },
  [CPC]: {
    ...(sharedColumnMetaDataMap[CPC] as ColumnMetaData),
    formatter: metricFormatter(CPC, true)
  },
  [CPM]: {
    ...(sharedColumnMetaDataMap[CPM] as ColumnMetaData),
    formatter: metricFormatter(CPM, true)
  },
  [CPX]: {
    ...(sharedColumnMetaDataMap[CPX] as ColumnMetaData),
    formatter: metricFormatter(CPX, true)
  },
  costPerConversion: {
    ...(sharedColumnMetaDataMap.costPerConversion as ColumnMetaData),
    formatter: metricFormatter('costPerConversion', true)
  },
  revenuePerDelivered: {
    ...(sharedColumnMetaDataMap.revenuePerDelivered as ColumnMetaData),
    formatter: metricFormatter('revenuePerDelivered', true)
  }
};

export const gbpOverrideColumnMetaDataMap: Partial<Record<P.ColumnType, ColumnMetaData>> = {
  [SPEND]: {
    ...(sharedColumnMetaDataMap[SPEND] as ColumnMetaData),
    formatter: metricFormatterGBP(SPEND)
  },
  [REVENUE]: {
    ...(sharedColumnMetaDataMap[REVENUE] as ColumnMetaData),
    formatter: metricFormatterGBP(REVENUE)
  },
  [CPC]: {
    ...(sharedColumnMetaDataMap[CPC] as ColumnMetaData),
    formatter: metricFormatterGBP(CPC)
  },
  [CPM]: {
    ...(sharedColumnMetaDataMap[CPM] as ColumnMetaData),
    formatter: metricFormatterGBP(CPM)
  },
  [CPX]: {
    ...(sharedColumnMetaDataMap[CPX] as ColumnMetaData),
    formatter: metricFormatterGBP(CPX)
  },
  costPerConversion: {
    ...(sharedColumnMetaDataMap.costPerConversion as ColumnMetaData),
    formatter: metricFormatterGBP('costPerConversion')
  },
  revenuePerDelivered: {
    ...(sharedColumnMetaDataMap.revenuePerDelivered as ColumnMetaData),
    formatter: metricFormatterGBP('revenuePerDelivered')
  }
};

export const getCompanyChannelColumnMetaData = (
  columnMetaDataMap: Partial<Record<P.ColumnType, ColumnMetaData>>,
  currencyCode: string
): Partial<Record<P.ColumnType, ColumnMetaData>> => {
  let overrideMap;
  if (isCAD(currencyCode)) {
    overrideMap = cadOverrideColumnMetaDataMap;
  } else if (isGBP(currencyCode)) {
    overrideMap = gbpOverrideColumnMetaDataMap;
  }
  if (!overrideMap) {
    return columnMetaDataMap;
  }
  const resolvedOverrideMap = overrideMap;
  const target = { ...columnMetaDataMap };
  Object.keys(target).forEach(key => {
    if (Object.hasOwn(resolvedOverrideMap, key)) {
      target[key] = resolvedOverrideMap[key];
    }
  });
  return target;
};
// Can replace all other versions of this function, TODO delete others
export const getDimensionCell = (
  dimensionData: DimensionMap,
  dimensionHeader: DimensionColumn,
  fiscalCal: CompanyFiscalCal,
  adInfoMap?: Record<string, AdInfoRow>,
  company?: string
): CellData => {
  const { dimensionVarName, dimensionTypeName } = dimensionHeader;
  const resolvedDimensionData = dimensionData as Record<DimensionColNames, string>;
  const dimensionValue = resolvedDimensionData[dimensionVarName];
  const defaultCell = {
    value: dimensionValue,
    label: dimensionValue
  };
  const iconCell = getDimensionIconCell(dimensionVarName, resolvedDimensionData, company);
  if (iconCell.url) {
    return {
      label: dimensionValue,
      value: dimensionValue,
      ...iconCell
    };
  }
  if (dimensionTypeName === 'Ad Name' && dimensionValue && adInfoMap && adInfoMap[dimensionValue]) {
    const adInfo = adInfoMap[dimensionValue];
    const url = `${CDN}/creativeAssets/metaThumbnails/${adInfo.company}/${adInfo.account_id}/${adInfo.ad_id}.jpeg`;
    return {
      label: dimensionValue,
      overlayText: dimensionValue,
      value: dimensionValue,
      url,
      adInfo
    };
  }
  if (dimensionTypeName === 'Ad Name') {
    return {
      label: dimensionValue,
      overlayText: dimensionValue, // allows tooltip for longer ad names
      value: dimensionValue
    };
  }
  if (dimensionTypeName === 'Date' || dimensionTypeName === 'ComparisonDate') {
    if (!dimensionHeader.dateGrouping) {
      return defaultCell;
    }
    const capitalizedDateGrouping = capitalize(dimensionHeader.dateGrouping);
    return {
      label: formatDateLabel(dimensionValue, fiscalCal, capitalizedDateGrouping),
      value: dimensionValue
    };
  }
  return defaultCell;
};

export const getDataColMetaData = (
  channel: App2Channels,
  currencyCode: string
): ColumnMetaDataMap => {
  const items = getDataColForMetadata(channel);
  let overrideMap;

  if (isCAD(currencyCode)) {
    overrideMap = cadOverrideColumnMetaDataMap;
  } else if (isGBP(currencyCode)) {
    overrideMap = gbpOverrideColumnMetaDataMap;
  }

  const columnMetaDataMap: Partial<Record<P.ColumnType, ColumnMetaData>> = {};
  for (const { name: metric } of items) {
    if (!Object.hasOwn(sharedColumnMetaDataMap, metric)) continue;
    columnMetaDataMap[metric] =
      overrideMap && Object.hasOwn(overrideMap, metric)
        ? overrideMap[metric]
        : sharedColumnMetaDataMap[metric];
  }
  return withRatioAggregators(columnMetaDataMap);
};

export const getDimensionColMetaData = (channel: App2Channels): ColumnMetaDataMap => {
  const dimensionColMetaDataMap: Partial<Record<string, ColumnMetaData>> = {
    ...sharedDimensionColumnMetaDataMap
  };
  for (const col of getDimensionColForMetadata(channel)) {
    dimensionColMetaDataMap[col.prettyName] = {
      contentType: 'text',
      name: col.name,
      prettyName: col.prettyName,
      iconStyle: getDimensionIcon(col.name)?.style
    };
  }
  return dimensionColMetaDataMap;
};

interface AmazonKpiSettingsArgs {
  pickerKPI?: string;
  setPickerLag: (lag: LagOption) => void;
  setTablePresetName: (name: string) => void;
}

/**
 * The backend only emits the Amazon sales KPIs at a 14d lag and by Network, so selecting one
 * forces both. Without this, other lags miss the `${kpi}_${lag}` fetch key and the column is blank.
 */
export const forceAmazonKpiSettings = (company: string, args: AmazonKpiSettingsArgs) => {
  const { pickerKPI, setPickerLag, setTablePresetName } = args;
  if (
    pickerKPI &&
    [getAmazonTransactionsKPI(company), getAmazonNTBTransactionsKPI(company)].includes(pickerKPI)
  ) {
    setTablePresetName('Network');
    setPickerLag('14d');
  }
};
